/**
 * Query keys, in one place.
 *
 * Mirrors `apps/mobile/src/hooks/keys.ts`. Every key is a tuple starting with a stable prefix so
 * a mutation can invalidate a whole family (`queryKeys.scheduleAll`) without knowing which window
 * a screen happens to be looking at.
 */
export const queryKeys = {
  me: ["me"] as const,
  businesses: ["me", "businesses"] as const,
  /** The businesses the account can join without a code (`GET /v1/me/join-offers`). */
  joinOffers: ["me", "join-offers"] as const,
  participants: ["me", "participants"] as const,
  scheduleAll: ["me", "schedule"] as const,
  schedule: (from: string, to: string) => ["me", "schedule", from, to] as const,
  credits: ["me", "credits"] as const,
  /**
   * The member home of one business (`GET /v1/me/businesses/:id/home`). Everything that moves a
   * booking or a balance invalidates `memberHomeAll`, so the home never says "Free to change" for a
   * session that was cancelled on another screen.
   */
  memberHomeAll: ["me", "member-home"] as const,
  memberHome: (businessId: string) => ["me", "member-home", businessId] as const,
  /** The health questions and terms of one business, and who still answers (C2). */
  memberHealthForm: (businessId: string) => ["me", "health-form", businessId] as const,
  /** Book again on the client home (B1), under the member home: whatever moves the home moves it too. */
  bookAgain: (businessId: string) => ["me", "member-home", businessId, "book-again"] as const,
  payments: ["me", "payments"] as const,
  paymentRequests: ["me", "payment-requests"] as const,
  payment: (paymentId: string) => ["payments", paymentId] as const,
  notifications: ["me", "notifications"] as const,
  /**
   * Messaging. The inbox is one query for both sides of the app, like the notification feed: a
   * conversation is addressed to a person, and which rows each area shows is the area's business.
   */
  conversations: ["me", "conversations"] as const,
  messagesAll: ["conversations"] as const,
  messages: (conversationId: string) => ["conversations", conversationId, "messages"] as const,
  /** Support requests to Teamna (support-system.md). */
  supportUnread: ["support", "unread"] as const,
  supportTickets: (businessId: string) => ["support", businessId, "tickets"] as const,
  supportTicket: (businessId: string, ticketId: string) => ["support", businessId, "ticket", ticketId] as const,
  /** AI Assistant (ai-assistant-v1.md): the history and one conversation of the signed-in person. */
  assistantAll: (businessId: string) => ["assistant", businessId] as const,
  assistantConversations: (businessId: string, q: string) => ["assistant", businessId, "conversations", q] as const,
  assistantConversationsAll: (businessId: string) => ["assistant", businessId, "conversations"] as const,
  assistantConversation: (businessId: string, conversationId: string) => ["assistant", businessId, "conversation", conversationId] as const,
  /** Suggestions by AI Assistant on Home. */
  assistantCards: (businessId: string) => ["assistant", businessId, "cards"] as const,
  groups: (businessId: string) => ["businesses", businessId, "groups"] as const,
  packages: (businessId: string) => ["businesses", businessId, "packages"] as const,
  /**
   * Availability sits on its own root rather than under `businesses`, so that a booking can
   * invalidate every open window in one call without also throwing away the class types and the
   * package list, which did not change and are the slowest things on the Book tab to fetch.
   */
  sessionsAll: ["sessions"] as const,
  sessions: (businessId: string, from: string, to: string, groupId: string | null, participantId: string | null = null) =>
    ["sessions", businessId, from, to, groupId, participantId] as const,

  /**
   * The owner side, under its own prefix.
   *
   * Every family has an `...All` tuple next to it, because a move or a cancel changes a week the
   * screen is not necessarily looking at. Invalidating `ownerSessionsAll` refreshes whichever week
   * happens to be mounted without the mutation having to know which one that is.
   */
  /**
   * The staff side: the day of the person who runs sessions, and one session with its roster for
   * the screen that takes attendance. Owners read one session through the same key, because the
   * route is shared. `all` refreshes both after a mark.
   */
  staff: {
    all: ["staff"] as const,
    today: (businessId: string) => ["staff", "today", businessId] as const,
    session: (businessId: string, sessionId: string) => ["staff", "session", businessId, sessionId] as const,
  },

  /**
   * Coaches add their own swimmers: the coach's list, the owner's names to link, and the "Ask
   * coaches" card. `all` refreshes the three after any change on either side.
   */
  coachParticipants: {
    all: (businessId: string) => ["coach-participants", businessId] as const,
    mine: (businessId: string) => ["coach-participants", businessId, "mine"] as const,
    owner: (businessId: string) => ["coach-participants", businessId, "owner"] as const,
    ask: (businessId: string) => ["coach-participants", businessId, "ask"] as const,
  },

  /** The find page of "Ask the parents" (`/find/:code`), for a signed-in parent. */
  find: {
    page: (code: string) => ["find", code] as const,
    children: (code: string, q: string) => ["find", code, "children", q] as const,
  },

  owner: {
    business: (businessId: string) => ["owner", "business", businessId] as const,
    sessions: (businessId: string, from: string, to: string) =>
      ["owner", "sessions", businessId, from, to] as const,
    sessionsAll: ["owner", "sessions"] as const,
    attendees: (businessId: string, sessionId: string) =>
      ["owner", "attendees", businessId, sessionId] as const,
    attendeesAll: ["owner", "attendees"] as const,
    /**
     * The level's swimmers with no booking in the session ("Did not book"). Under `attendees`, so
     * every write that changes who is coming refreshes it too. Coaches read it in Someone else came.
     */
    notBooked: (businessId: string, sessionId: string) => ["owner", "attendees", businessId, sessionId, "not-booked"] as const,
    members: (businessId: string) => ["owner", "members", businessId] as const,
    memberCredits: (businessId: string, membershipId: string) =>
      ["owner", "member-credits", businessId, membershipId] as const,
    memberCreditsAll: ["owner", "member-credits"] as const,
    enrollments: (businessId: string) => ["owner", "enrollments", businessId] as const,
    groups: (businessId: string) => ["owner", "groups", businessId] as const,
    /** The booking rules of one group (B3, migration 0073). */
    classRules: (businessId: string, groupId: string) => ["owner", "class-rules", businessId, groupId] as const,
    templates: (businessId: string) => ["owner", "schedule-templates", businessId] as const,
    products: (businessId: string) => ["owner", "package-products", businessId] as const,
    payments: (businessId: string) => ["owner", "payments", businessId] as const,
    /** Classes per staff member in one month (`yyyy-MM`), for pay. */
    staffClasses: (businessId: string, month: string) => ["owner", "staff-classes", businessId, month] as const,
    staffMember: (businessId: string, userId: string) => ["owner", "staff-member", businessId, userId] as const,
    staffActivity: (businessId: string, userId: string) => ["owner", "staff-activity", businessId, userId] as const,
    recurringFees: (businessId: string) => ["owner", "recurring-fees", businessId] as const,
    invites: (businessId: string) => ["owner", "invites", businessId] as const,
    /** The live logo and the recent uploads (Settings, Brand). */
    logos: (businessId: string) => ["owner", "logos", businessId] as const,
    /** Everyone who can have a profile photo (the Team photo tool, migration 0029). */
    photoPeople: (businessId: string) => ["owner", "photo-people", businessId] as const,
    announcementAudience: (businessId: string) =>
      ["owner", "announcement-audience", businessId] as const,
    /** One participant's profile (balance, upcoming bookings, ledger), for the member page. */
    participantProfile: (businessId: string, participantId: string) =>
      ["owner", "participant-profile", businessId, participantId] as const,
    participantProfileAll: ["owner", "participant-profile"] as const,
    /** The days a package sold now would run (#ai-feedback 2026-10-26, card #1). Under the profile key, so a sale refreshes it. */
    packageStart: (businessId: string, participantId: string, productId: string) =>
      ["owner", "participant-profile", businessId, participantId, "package-start", productId] as const,
    /** Health notes (C2): the form setup, and one person's flag or answers. */
    healthForm: (businessId: string) => ["owner", "health-form", businessId] as const,
    participantHealth: (businessId: string, participantId: string) => ["owner", "participant-health", businessId, participantId] as const,
    /** The business's own Teamna plan (the Billing page). */
    billing: (businessId: string) => ["owner", "billing", businessId] as const,
    /** The business's own payment account at Stripe (the Get paid page). */
    payoutAccount: (businessId: string) => ["owner", "payout-account", businessId] as const,
  },


  /**
   * The setup checklist and the Home page, under `/business/businesses/:id`. One prefix per
   * business, so a save on any setup page can refresh the whole checklist with one call: every
   * step's "done" is derived from the data the other pages write.
   */
  setup: {
    all: (businessId: string) => ["setup", businessId] as const,
    progress: (businessId: string) => ["setup", businessId, "progress"] as const,
    team: (businessId: string) => ["setup", businessId, "team"] as const,
    contacts: (businessId: string) => ["setup", businessId, "imported-contacts"] as const,
    groupLink: (businessId: string, linkId: string) => ["setup", businessId, "group-link", linkId] as const,
    /** The WhatsApp sync tool: its state, and the people of each view. Under `setup`, so a sort refreshes the checklist. */
    whatsAppSync: (businessId: string) => ["setup", businessId, "whatsapp-sync"] as const,
    syncPeople: (businessId: string, view: string) => ["setup", businessId, "whatsapp-sync", "people", view] as const,
    syncFamilies: (businessId: string) => ["setup", businessId, "whatsapp-sync", "families"] as const,
    timetable: (businessId: string) => ["setup", businessId, "timetable"] as const,
    template: (businessId: string) => ["setup", businessId, "timetable-template"] as const,
    /** The owner timetable's week editor, and the dry run of its draft (keyed by the draft). */
    timetableEditor: (businessId: string, month = "now") => ["setup", businessId, "timetable-editor", month] as const,
    /** Not under `setup`: a save elsewhere must not run the dry run again. */
    timetablePreview: (businessId: string, draft: string) => ["timetable-preview", businessId, draft] as const,
    packages: (businessId: string) => ["setup", businessId, "packages"] as const,
    invitePreview: (businessId: string) => ["setup", businessId, "invite-preview"] as const,
    inviteStatus: (businessId: string) => ["setup", businessId, "invite-status"] as const,
    pendingMembers: (businessId: string) => ["setup", businessId, "pending-members"] as const,
    participants: (businessId: string) => ["setup", businessId, "participants"] as const,
    balances: (businessId: string) => ["setup", businessId, "starting-balances"] as const,
    schedule: (businessId: string, from: string, to: string) =>
      ["setup", businessId, "schedule", from, to] as const,
    /** The Families page and the owner Home: under `setup`, so every setup save refreshes them too. */
    families: (businessId: string) => ["setup", businessId, "families"] as const,
    familyInvites: (businessId: string) => ["setup", businessId, "family-invites"] as const,
    home: (businessId: string) => ["setup", businessId, "home"] as const,
    /** The open spots card on Home (H2): under `home`, so whatever refreshes Home refreshes it. */
    homeOpenSpots: (businessId: string) => ["setup", businessId, "home", "open-spots"] as const,
    /** The Clients table (C1), one entry per filter. Under `setup`, like the families. */
    clients: (businessId: string, filter: string) => ["setup", businessId, "clients", filter] as const,
  },
  /** Cover requests between staff (migration 0072): the reader's list, for every surface. */
  covers: (businessId: string) => ["covers", businessId] as const,
  /** No-show and late-cancel fees to confirm (I2). */
  fees: (businessId: string) => ["fees", businessId] as const,
  /** The owner's To do list on Home and its menu badge. Every successful write reloads it (query-client.ts). */
  todo: {
    all: ["todo"] as const,
    owner: (businessId: string) => ["todo", businessId] as const,
  },
};

/**
 * Every window of the business schedule with its registers (`setup.schedule`), for any business.
 * The Today card on Home and the Calendar's Today view read it, so every write that changes a
 * session, a booking or a mark refreshes it: `queryClient.invalidateQueries(businessScheduleQueries)`.
 */
export const businessScheduleQueries = {
  predicate: (query: { queryKey: readonly unknown[] }) => query.queryKey[0] === "setup" && query.queryKey[2] === "schedule",
};

/**
 * Every Did not book list (`owner.notBooked`), for any session. It is built from the participants and
 * the waiting names of a level, so a write that adds, moves or removes one of them refreshes it:
 * `queryClient.invalidateQueries(notBookedQueries)`. Session and booking writes refresh it through
 * `owner.attendeesAll`.
 */
export const notBookedQueries = {
  predicate: (query: { queryKey: readonly unknown[] }) => query.queryKey[0] === "owner" && query.queryKey[1] === "attendees" && query.queryKey[4] === "not-booked",
};
